import type { ICertificateEmployeeDTO } from '@sharedInterfaces/ICertificate';
import {
    EGender,
    type IAvailabilityDTO,
    type IEmployeeDTO,
    type IEmployeeRoleReference,
} from '@sharedInterfaces/IEmployee';
import type { ISkillReferenceDTO } from '@sharedInterfaces/ISkill';
import { type ELanguage, findMatchingLanguage } from '@sharedInterfaces/Language/languageHelper';
export interface EmployeeState extends IEmployeeDTO {
    language: ELanguage;
    unknownSkillCount: number;
    primaryRoleId: number | null;
    featuredCertificateId: number | null;
    superPowerOrder: number[];
}

export const initialEmployeeState: EmployeeState = {
    id: -1,
    title: '',
    description: '',
    firstName: '',
    lastName: '',
    gender: EGender.OTHER,
    email: '',
    phoneNumber: undefined,
    linkedInUrl: undefined,
    slackUrl: undefined,
    websiteUrl: undefined,
    language: findMatchingLanguage(window.navigator.languages),
    certificates: [],
    skills: [],
    similarUsers: [],
    availability: { availability: 0, workDayCount: 0 },
    products: [],
    projects: [],
    opportunities: [],
    competences: [],
    roles: [],
    salesIdea: '',
    pictureId: null,
    unknownSkillCount: 0,
    primaryRoleId: null,
    featuredCertificateId: null,
    jobTitle: '',
    version: 0,
    superPowerOrder: [],
};
export enum EmployeeActionTypes {
    SET_EMPLOYEE = 'SET_EMPLOYEE',
    SET_EMPLOYEE_AVAILABILITY = 'SET_EMPLOYEE_AVAILABILITY',
    SET_EMPLOYEE_LANG = 'SET_EMPLOYEE_LANG',
    SET_EMPLOYEE_DESCRIPTION = 'SET_EMPLOYEE_DESCRIPTION',
    SET_EMPLOYEE_CERTIFICATES = 'SET_EMPLOYEE_CERTIFICATES',
    SET_EMPLOYEE_SKILLS = 'SET_EMPLOYEE_SKILLS',
    SET_EMPLOYEE_PICTURE_ID = 'SET_EMPLOYEE_PICTURE_ID',
    SET_EMPLOYEE_UNKNOWN_SKILL_COUNT = 'SET_EMPLOYEE_UNKNOWN_SKILL_COUNT',
    SET_EMPLOYEE_ROLES = 'SET_EMPLOYEE_ROLES',
    SET_EMPLOYEE_NAMES = 'SET_EMPLOYEE_NAMES',
    SET_EMPLOYEE_GENDER = 'SET_EMPLOYEE_GENDER',
    SET_EMPLOYEE_PHONE_NUMBER = 'SET_EMPLOYEE_PHONE_NUMBER',
    SET_EMPLOYEE_LINKEDIN_URL = 'SET_EMPLOYEE_LINKEDIN_URL',
    SET_EMPLOYEE_SLACK_URL = 'SET_EMPLOYEE_SLACK_URL',
    SET_EMPLOYEE_WEBSITE_URL = 'SET_EMPLOYEE_WEBSITE_URL',
    SET_PRIMARY_ROLE_ID = 'SET_PRIMARY_ROLE_ID',
    SET_FEATURED_CERTIFICATE_ID = 'SET_FEATURED_CERTIFICATE_ID',
}

// Spezifische Aktionstypen
interface ISetEmployeeAction {
    type: EmployeeActionTypes.SET_EMPLOYEE;
    payload: EmployeeState;
}

interface ISetEmployeeAvailabilityAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_AVAILABILITY;
    payload: IAvailabilityDTO;
}

interface ISetEmployeeLangAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_LANG;
    payload: ELanguage;
}

interface ISetEmployeeDescriptionAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_DESCRIPTION;
    payload: string;
}
interface ISetEmployeePictureIdAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_PICTURE_ID;
    payload: string | null;
}

interface ISetEmployeeCertificatesAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_CERTIFICATES;
    payload: ICertificateEmployeeDTO[];
}

interface ISetEmployeeSkillsAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_SKILLS;
    payload: ISkillReferenceDTO[];
}
interface ISetEmployeeUnknownSkillCount {
    type: EmployeeActionTypes.SET_EMPLOYEE_UNKNOWN_SKILL_COUNT;
    payload: number;
}

interface ISetEmployeeRolesAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_ROLES;
    payload: IEmployeeRoleReference[];
}

interface ISetEmployeeNamesAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_NAMES;
    payload: { firstname: string; lastname: string };
}

interface ISetEmployeeGenderAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_GENDER;
    payload: EGender;
}

interface ISetEmployeePhoneNumberAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_PHONE_NUMBER;
    payload: string | undefined;
}

interface ISetEmployeeLinkedInUrlAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_LINKEDIN_URL;
    payload: string | undefined;
}

interface ISetEmployeeSlackUrlAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_SLACK_URL;
    payload: string | undefined;
}

interface ISetEmployeeWebsiteUrlAction {
    type: EmployeeActionTypes.SET_EMPLOYEE_WEBSITE_URL;
    payload: string | undefined;
}

interface ISetPrimaryRoleIdAction {
    type: EmployeeActionTypes.SET_PRIMARY_ROLE_ID;
    payload: number | null;
}

interface ISetFeaturedCertificateIdAction {
    type: EmployeeActionTypes.SET_FEATURED_CERTIFICATE_ID;
    payload: number | null;
}

// Kombinierter Aktionstyp
type EmployeeAction =
    | ISetEmployeeAction
    | ISetEmployeeAvailabilityAction
    | ISetEmployeeLangAction
    | ISetEmployeeDescriptionAction
    | ISetEmployeeCertificatesAction
    | ISetEmployeeSkillsAction
    | ISetEmployeePictureIdAction
    | ISetEmployeeUnknownSkillCount
    | ISetEmployeeRolesAction
    | ISetEmployeeNamesAction
    | ISetEmployeeGenderAction
    | ISetEmployeePhoneNumberAction
    | ISetEmployeeLinkedInUrlAction
    | ISetEmployeeSlackUrlAction
    | ISetEmployeeWebsiteUrlAction
    | ISetPrimaryRoleIdAction
    | ISetFeaturedCertificateIdAction;

export const employeeReducer = (state = initialEmployeeState, action: EmployeeAction): EmployeeState => {
    switch (action.type) {
        case EmployeeActionTypes.SET_EMPLOYEE:
            return {
                ...state,
                ...action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_AVAILABILITY:
            return {
                ...state,
                availability: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_LANG:
            return {
                ...state,
                language: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_DESCRIPTION:
            return {
                ...state,
                description: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_CERTIFICATES:
            return {
                ...state,
                certificates: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_SKILLS:
            return {
                ...state,
                skills: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_PICTURE_ID:
            return {
                ...state,
                pictureId: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_UNKNOWN_SKILL_COUNT:
            return {
                ...state,
                unknownSkillCount: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_ROLES:
            return {
                ...state,
                roles: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_NAMES:
            return {
                ...state,
                firstName: action.payload.firstname,
                lastName: action.payload.lastname,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_GENDER:
            return {
                ...state,
                gender: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_PHONE_NUMBER:
            return {
                ...state,
                phoneNumber: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_LINKEDIN_URL:
            return {
                ...state,
                linkedInUrl: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_SLACK_URL:
            return {
                ...state,
                slackUrl: action.payload,
            };
        case EmployeeActionTypes.SET_EMPLOYEE_WEBSITE_URL:
            return {
                ...state,
                websiteUrl: action.payload,
            };
        case EmployeeActionTypes.SET_PRIMARY_ROLE_ID:
            return {
                ...state,
                primaryRoleId: action.payload,
            };
        case EmployeeActionTypes.SET_FEATURED_CERTIFICATE_ID:
            return {
                ...state,
                featuredCertificateId: action.payload,
            };
        default:
            return state;
    }
};

// SET_EMPLOYEE
export const setEmployeeStore = (state: EmployeeState): ISetEmployeeAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE,
    payload: state,
});

// Action Creator for setting employee
export const setEmployee = (employeeState: EmployeeState): ISetEmployeeAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE,
    payload: employeeState,
});

// Action Creator for setting employee availability
export const setEmployeeAvailability = (availability: IAvailabilityDTO): ISetEmployeeAvailabilityAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_AVAILABILITY,
    payload: availability,
});

// Action Creator for setting employee language
export const setEmployeeLang = (language: ELanguage): ISetEmployeeLangAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_LANG,
    payload: language,
});

// Action Creator for setting employee description
export const setEmployeeDescription = (description: string): ISetEmployeeDescriptionAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_DESCRIPTION,
    payload: description,
});

// Action Creator for setting employee picture ID
export const setEmployeePictureId = (pictureId: string | null): ISetEmployeePictureIdAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_PICTURE_ID,
    payload: pictureId,
});

// Action Creator for setting employee certificates
export const setEmployeeCertificates = (certificates: ICertificateEmployeeDTO[]): ISetEmployeeCertificatesAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_CERTIFICATES,
    payload: certificates,
});

// Action Creator for setting employee skills
export const setEmployeeSkills = (skills: ISkillReferenceDTO[]): ISetEmployeeSkillsAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_SKILLS,
    payload: skills,
});

// Action Creator for setting unknown skill count
export const setEmployeeUnknownSkillCount = (count: number): ISetEmployeeUnknownSkillCount => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_UNKNOWN_SKILL_COUNT,
    payload: count,
});

// Action Creator for setting employee roles
export const setEmployeeRoles = (roles: IEmployeeRoleReference[]): ISetEmployeeRolesAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_ROLES,
    payload: roles,
});

export const setEmployeeNames = ({
    firstname,
    lastname,
}: {
    firstname: string;
    lastname: string;
}): ISetEmployeeNamesAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_NAMES,
    payload: { firstname, lastname },
});

// Action Creator for setting employee gender
export const setEmployeeGender = (gender: EGender): ISetEmployeeGenderAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_GENDER,
    payload: gender,
});

// Action Creator for setting employee phone number
export const setEmployeePhoneNumber = (phoneNumber: string | undefined): ISetEmployeePhoneNumberAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_PHONE_NUMBER,
    payload: phoneNumber,
});

// Action Creator for setting employee LinkedIn URL
export const setEmployeeLinkedInUrl = (linkedInUrl: string | undefined): ISetEmployeeLinkedInUrlAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_LINKEDIN_URL,
    payload: linkedInUrl,
});

// Action Creator for setting employee Slack URL
export const setEmployeeSlackUrl = (slackUrl: string | undefined): ISetEmployeeSlackUrlAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_SLACK_URL,
    payload: slackUrl,
});

// Action Creator for setting employee Website URL
export const setEmployeeWebsiteUrl = (websiteUrl: string | undefined): ISetEmployeeWebsiteUrlAction => ({
    type: EmployeeActionTypes.SET_EMPLOYEE_WEBSITE_URL,
    payload: websiteUrl,
});

// Action Creator for setting primary role ID
export const setPrimaryRoleId = (roleId: number | null): ISetPrimaryRoleIdAction => ({
    type: EmployeeActionTypes.SET_PRIMARY_ROLE_ID,
    payload: roleId,
});

// Action Creator for setting featured certificate ID
export const setFeaturedCertificateId = (certificateId: number | null): ISetFeaturedCertificateIdAction => ({
    type: EmployeeActionTypes.SET_FEATURED_CERTIFICATE_ID,
    payload: certificateId,
});
